import { createFileRoute } from "@tanstack/react-router"
import { parseMarketplaceId } from "@nova-ai/shared"

import { AppShell } from "../components/layout/app-shell"
import { BaselinkerRefreshSection } from "../components/dashboard/baselinker-refresh-section"
import { MarketplaceMark } from "../components/marketplaces/marketplace-mark"
import { MarketplaceSwitch } from "../components/marketplaces/marketplace-switch"
import { humanizeMarketplaceId } from "../lib/marketplace"

/**
 * One shop's live-offer write surface: the Baselinker storage sync and the
 * live-offer rebuild, on its own page.
 *
 * It used to sit at the foot of the marketplace dashboard. It moved here because
 * it is the one place on that page that WRITES — it starts panel bulk operations
 * and marketplace uploads against live offers — and a control that changes a
 * seller account does not belong beneath revenue charts an operator scrolls past
 * all day. It sits under Work rather than Insights for the same reason.
 *
 * No `QueryState` wrapper: the section reads its own availability and already
 * renders its loading, unreachable, and unsupported states — a shell gate here
 * would only duplicate them.
 *
 * A marketplace reached from the switcher may not support either control; there
 * is no fleet-wide capability read to filter the list against, so the section
 * says so per shop.
 */
function StorageRefreshPage() {
  const { marketplace: raw } = Route.useParams()
  const marketplace = parseMarketplaceId(raw)

  return (
    <AppShell
      title="Storage refresh"
      description={`Push storage prices and quantities onto live offers — ${humanizeMarketplaceId(marketplace)}.`}
      icon={<MarketplaceMark marketplace={marketplace} />}
      actions={<MarketplaceSwitch marketplace={marketplace} to="/storage-refresh/$marketplace" />}
      fullWidth
    >
      {/* A param switch keeps this route mounted. Remount the write surface so
          its preview, confirmation, mode and floor all belong to this shop. */}
      <BaselinkerRefreshSection key={marketplace} marketplace={marketplace} />
    </AppShell>
  )
}

export const Route = createFileRoute("/storage-refresh/$marketplace/")({
  component: StorageRefreshPage,
})
